import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Signal } from "lucide-react";
import StatusCard from "@/components/StatusCard";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { STORAGE_KEYS, getFromStorage, saveToStorage } from "@/utils/storage";
import moment from "moment";
import "moment/locale/tr";
import { useDispatch, useSelector } from "react-redux";
import { checkNodeHealthServiceRequest } from "../services/node/actions";

moment.locale("tr");

function getStatusBorderClass(status) {
  if (!status || typeof status !== "string")
    return "border-l-8 border-gray-500";
  switch (status.toLowerCase()) {
    case "ok":
      return "border-l-8 border-green-500";
    case "yoğun":
      return "border-l-8 border-yellow-500";
    case "çöktü":
      return "border-l-8 border-red-500";
    default:
      return "border-l-8 border-gray-500";
  }
}

function getStatusColor(status) {
  if (!status || typeof status !== "string") return "gray";
  switch (status.toLowerCase()) {
    case "ok":
      return "green";
    case "yoğun":
      return "yellow";
    case "çöktü":
      return "red";
    default:
      return "gray";
  }
}

export default function NodeStatus() {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(() =>
    getFromStorage(STORAGE_KEYS.CURRENT_PAGE, 1)
  );
  const [error, setError] = useState(null);
  const itemsPerPage = 4;

  const { nodeHealth = [], loading = false } = useSelector(
    (state) => state.node || {}
  );

  useEffect(() => {
    fetchNodeStatus();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  const fetchNodeStatus = () => {
    setError(null);
    const attributes = [
      "nodeId",
      "status",
      "metrics.cpu_usage",
      "metrics.memory_usage",
      "isLeader",
      "uptime",
      "lastUpdate",
    ];
    dispatch(checkNodeHealthServiceRequest(attributes));
  };

  const handleRefresh = () => {
    fetchNodeStatus();
  };

  const nodeHealthArray = Array.isArray(nodeHealth) ? nodeHealth : [];
  const totalPages = Math.ceil((nodeHealthArray.length || 0) / itemsPerPage);
  const paginatedNodes = nodeHealthArray.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatUptime = (seconds) => {
    const duration = moment.duration(seconds, "seconds");
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    const secs = duration.seconds();
    return `${hours} saat ${minutes} dakika ${secs} saniye`;
  };

  return (
    <div className="p-6 w-full min-w-0">
      <div className="flex items-center gap-3 mb-6 justify-between">
        <div className="flex items-center gap-3">
          <Signal />
          <h1 className="text-2xl font-bold">Düğümlerin Durumu</h1>
        </div>
        <Button variant="refresh" onClick={handleRefresh} disabled={loading} />
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Hata</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-0">
        {paginatedNodes.map((node, index) => (
          <div key={index} className="flex justify-center">
            <StatusCard
              title={node.nodeId}
              description={node.ip}
              status={node.status}
              statusType="badge"
              statusColor={getStatusColor(node.status)}
              className={`w-96 ${getStatusBorderClass(node.status)}`}
              additionalInfo={[
                {
                  label: "Liderlik",
                  value: node.isLeader ? "Lider" : "Takipçi",
                },
                {
                  label: "Uptime",
                  value: formatUptime(node.uptime),
                },
                {
                  label: "CPU Kullanımı",
                  value: `${node.metrics?.cpu_usage || 0}%`,
                },
                {
                  label: "Bellek Kullanımı",
                  value: `${node.metrics?.memory_usage || 0}%`,
                },
                {
                  label: "Son Güncelleme",
                  value: moment(node.lastUpdate).format(
                    "DD MMMM YYYY HH:mm:ss"
                  ),
                },
              ]}
            />
          </div>
        ))}
      </div>

      {totalPages > 0 && (
        <div className="mt-6 flex justify-center w-full">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  className={
                    currentPage === 1 ? "pointer-events-none opacity-50" : ""
                  }
                />
              </PaginationItem>
              {[...Array(totalPages)].map((_, i) => (
                <PaginationItem key={`page-${i}`}>
                  <PaginationLink
                    href="#"
                    isActive={currentPage === i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  className={
                    currentPage === totalPages
                      ? "pointer-events-none opacity-50"
                      : ""
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
